import type { ReportColumn } from "@/profitability/types";

const NUMERIC = ["number", "currency", "percent"];
/** How many of a row's own fields ride along under its name. */
const INLINE_DETAILS = 3;

export const isNumericColumn = (column: ReportColumn) =>
  NUMERIC.includes(column.type);

/**
 * How a row divides: what names it, what it earned, and what else is worth
 * saying underneath.
 *
 * The figure the table sorts by leads the money, because that is the column the
 * report is pointing at. On a profitability report sorted worst-first, that is
 * gross profit, and the rest read as the workings behind it.
 */
export const splitColumns = (
  columns: ReportColumn[],
  {
    leadKey,
    titleKey,
    subtitleKey,
  }: { leadKey?: string; titleKey?: string; subtitleKey?: string } = {},
): {
  identifier?: ReportColumn;
  subtitle?: ReportColumn;
  details: ReportColumn[];
  /** Text fields past the few that fit under the name, shown as columns. */
  extra: ReportColumn[];
  money: ReportColumn[];
} => {
  const numeric = columns.filter(isNumericColumn);
  const lead = numeric.find((column) => column.key === leadKey);
  const money = lead
    ? [lead, ...numeric.filter((column) => column !== lead)]
    : numeric;
  const text = columns.filter((column) => !isNumericColumn(column));
  // What names a row is what a person would call it. The script says so when
  // it knows; otherwise the first text column has to stand in, which on a job
  // report means an id.
  const identifier = text.find((column) => column.key === titleKey) ?? text[0];
  const subtitle = text.find((column) => column.key === subtitleKey);
  const details = text.filter(
    (column) => column !== identifier && column !== subtitle,
  );
  return {
    identifier,
    subtitle,
    details: details.slice(0, INLINE_DETAILS),
    // A table shows every column it declares. Past the few that ride under
    // the name, the rest get columns of their own rather than disappearing.
    extra: details.slice(INLINE_DETAILS),
    money,
  };
};

/**
 * The scroller's height.
 *
 * A table with the pane to itself takes about the height of the content area,
 * so a short scroll of the page puts its head at the top and its foot clear of
 * the help bubble, with the pagination controls in view and the rows scrolling
 * inside it. A table sharing the grid keeps the short cap instead, so the cards
 * under it stay reachable.
 */
export const tableHeight = (fill: boolean, paged: boolean) => {
  if (!fill) return paged ? "h-96" : "max-h-96";
  return paged
    ? "h-[calc(100cqh-19rem)] min-h-96"
    : "max-h-[calc(100cqh-19rem)]";
};
